import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import { useUserPersona } from '../context/UserPersonaContext';
import { useImpersonation } from '../context/ImpersonationContext';
import registrationService from '../services/registrationService';
import usfsService from '../services/usfsService';
import { getAuth, updateProfile as updateFirebaseProfile } from 'firebase/auth';
import { app } from '../config/firebaseConfig';
import USFSLinkDialog from '../components/USFSLinkDialog';
import USFSSyncStatus from '../components/USFSSyncStatus';
import WidgetCard from '../components/WidgetCard';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Avatar,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  FormControlLabel,
  Switch,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  EmojiEvents as TrophyIcon,
  Business as ClubIcon,
  Link as LinkIcon,
} from '@mui/icons-material';

export default function Profile() {
  const navigate = useNavigate();
  const { user, getIdToken, loading: authLoading } = useFirebaseAuth();
  const { getNavigationLabels, getPersonaInfo, refreshUserData, skaters } = useUserPersona();
  const { isImpersonating, impersonatedUser } = useImpersonation();
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profileForm, setProfileForm] = useState({});
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [usfsStatus, setUsfsStatus] = useState(null);
  const [usfsLoading, setUsfsLoading] = useState(false);
  const [usfsError, setUsfsError] = useState('');
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [clubSelectDialogOpen, setClubSelectDialogOpen] = useState(false);
  const [clubs, setClubs] = useState([]);
  const [selectedClubId, setSelectedClubId] = useState('');
  const [loadingClubs, setLoadingClubs] = useState(false);
  const [pendingCoachRequest, setPendingCoachRequest] = useState(null);

  // Get dynamic labels and persona info
  const navLabels = getNavigationLabels();
  const personaInfo = getPersonaInfo();

  const loadProfileData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = await getIdToken();
      if (!token) {
        throw new Error('Unable to get authentication token');
      }

      const profileData = await registrationService.getProfile(token);

      setProfile(profileData);
      // Set form data, ensuring isCoach reflects both toggle and role approval
      const hasCoachRole = profileData?.roles?.includes('coach');
      setProfileForm({
        ...(profileData || {}),
        isCoach: profileData?.isCoach || hasCoachRole,
      });

      // NOTE: Onboarding is now handled by OnboardingWizard in App.jsx
      // The old onboarding dialog has been disabled
    } catch (err) {
      // If 404, this is a brand new user - onboarding is handled by OnboardingWizard in App.jsx
      if (err.message?.includes('404') || err.message?.includes('not found')) {
        setProfile({});
        setProfileForm({
          displayName: user?.displayName || '',
        });
        // NOTE: Onboarding is now handled by OnboardingWizard in App.jsx
      } else {
        setError('Failed to load profile data');
        console.error('Error loading profile:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [getIdToken, user?.displayName]);

  const loadUSFSStatus = useCallback(async (options = {}) => {
    const { preserveError = false } = options;
    try {
      setUsfsLoading(true);
      if (!preserveError) {
        setUsfsError('');
      }
      const token = await getIdToken();
      if (!token) {
        throw new Error('Unable to get authentication token');
      }
      const statusData = await usfsService.getStatus(token);
      setUsfsStatus(statusData);
    } catch (err) {
      setUsfsError(err.message || 'Failed to load USFS status');
    } finally {
      setUsfsLoading(false);
    }
  }, [getIdToken]);

  useEffect(() => {
    // Don't redirect if auth is still loading
    if (authLoading) {
      return;
    }
    if (!user) {
      navigate('/');
      return;
    }
    loadProfileData();
    loadUSFSStatus();
  }, [user, navigate, loadProfileData, loadUSFSStatus, authLoading]);


  const handleProfileEdit = () => {
    const hasCoachRole = profile?.roles?.includes('coach');
    setProfileForm({
      ...(profile || {}),
      isCoach: profile?.isCoach || hasCoachRole,
    });
    setEditDialogOpen(true);
  };

  const loadClubs = async () => {
    try {
      setLoadingClubs(true);
      const token = await getIdToken();
      const response = await fetch(`${registrationService.getBaseUrl()}/clubs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setClubs(data.clubs || data || []);
      }
    } catch (err) {
      console.error('Error loading clubs:', err);
    } finally {
      setLoadingClubs(false);
    }
  };

  const handleProfileSave = async (overrideData = {}) => {
    try {
      setError(null);
      const token = await getIdToken();
      if (!token) {
        throw new Error('Unable to get authentication token');
      }

      const updatedProfile = { ...profileForm, ...overrideData };
      if (updatedProfile.isSkater && updatedProfile.parentName === updatedProfile.displayName) {
        updatedProfile.parentName = '';
      }
      if (updatedProfile.isCoach && !(updatedProfile.membershipNumber || updatedProfile.coachUsfsMemberId || '').toString().trim()) {
        setError('USFS Membership ID is required when registering as a coach.');
        return;
      }

      await registrationService.updateProfile(updatedProfile, token);
      if (user && updatedProfile.displayName && updatedProfile.displayName !== user.displayName) {
        const auth = getAuth(app);
        if (auth.currentUser) {
          await updateFirebaseProfile(auth.currentUser, { displayName: updatedProfile.displayName });
        }
      }
      setProfile(updatedProfile);
      setEditDialogOpen(false);
      setClubSelectDialogOpen(false);
      
      // Refresh persona context to update navigation labels
      await refreshUserData();
    } catch (err) {
      if (err.message?.includes('CLUB_REQUIRED') || err.message?.includes('Club association required')) {
        await loadClubs();
        setClubSelectDialogOpen(true);
        return;
      }
      if (err.message?.includes('COACH_USFS_ID_REQUIRED') || err.message?.includes('USFS Membership ID is required for coach')) {
        setError('USFS Membership ID is required for coach status. Enter your USFS member number in the coach field above.');
        return;
      }
      setError('Failed to update profile');
      console.error('Error updating profile:', err);
    }
  };

  const handleClubSelect = async () => {
    if (!selectedClubId) {
      setError('Please select a club');
      return;
    }
    // Find the club to get its interclubId
    const selectedClub = clubs.find(c => c.id === selectedClubId);
    await handleProfileSave({
      clubId: selectedClubId,
      interclubId: selectedClub?.interclubId || null,
    });
  };

  const handleProfileCancel = () => {
    setProfileForm(profile || {});
    setEditDialogOpen(false);
  };

  const handleOpenUSFSLink = () => {
    setLinkError('');
    setLinkDialogOpen(true);
  };

  const handleLinkUSFS = async ({ username, password, consent, autoSync }) => {
    try {
      setLinking(true);
      setLinkError('');
      const token = await getIdToken();
      if (!token) {
        throw new Error('Unable to get authentication token');
      }
      await usfsService.linkAccount({ username, password, consent, autoSync }, token);
      setLinkDialogOpen(false);
      await loadUSFSStatus();
    } catch (err) {
      setLinkError(err.message || 'Failed to link USFS account');
    } finally {
      setLinking(false);
    }
  };

  const handleManualSync = async () => {
    try {
      setSyncing(true);
      setUsfsError('');
      const token = await getIdToken();
      if (!token) {
        throw new Error('Unable to get authentication token');
      }
      await usfsService.triggerManualSync(token);
    } catch (err) {
      setUsfsError(err.message || 'Failed to sync USFS data');
    } finally {
      setSyncing(false);
      await loadUSFSStatus({ preserveError: true });
    }
  };

  const handleUnlink = async () => {
    if (!window.confirm('Unlink your USFS account? This only affects your personal account - skater USFS links are managed separately on each skater\'s page.')) {
      return;
    }
    try {
      setUnlinking(true);
      setUsfsError('');
      const token = await getIdToken();
      if (!token) {
        throw new Error('Unable to get authentication token');
      }
      await usfsService.unlinkAccount({}, token);
      await loadUSFSStatus();
    } catch (err) {
      setUsfsError(err.message || 'Failed to unlink USFS account');
    } finally {
      setUnlinking(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Compact Page Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <PersonIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {navLabels.profile}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage your account and preferences
            </Typography>
          </Box>
          <Box sx={{ ml: 'auto' }}>
            <Tooltip title={personaInfo.description} arrow>
              <Chip
                label={personaInfo.label}
                icon={<span style={{ fontSize: '1.2rem' }}>{personaInfo.icon}</span>}
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            </Tooltip>
          </Box>
        </Box>
        {/* Optional summary chips: account types */}
        <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
          {(profile?.isParent || profile?.roles?.includes('parent') || profile?.role === 'parent') && (
            <Chip label="Parent" size="small" color="primary" variant="outlined" sx={{ borderRadius: 2 }} />
          )}
          {(profile?.isCoach || profile?.roles?.includes('coach')) && (
            <Chip
              label={profile?.approvalStatus?.coach === 'approved' ? 'Coach' : 'Coach (Pending)'}
              size="small"
              color={profile?.approvalStatus?.coach === 'approved' ? 'success' : 'warning'}
              variant="outlined"
              sx={{ borderRadius: 2 }}
            />
          )}
          {(profile?.isSkater || profile?.roles?.includes('skater')) && (
            <Chip label="Adult Skater" size="small" color="info" variant="outlined" sx={{ borderRadius: 2 }} />
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Widget: Profile */}
        <Grid item xs={12} md={6}>
          <WidgetCard
            title="Profile Information"
            icon={<PersonIcon sx={{ color: 'white', fontSize: 28 }} />}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  bgcolor: isImpersonating ? 'warning.main' : 'primary.main',
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  mr: 2
                }}
              >
                {(isImpersonating ? (profile?.displayName || profile?.firstName || impersonatedUser?.displayName) : user.displayName)?.[0] || 
                 (isImpersonating ? (profile?.email || impersonatedUser?.email) : user.email)?.[0] || 'U'}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {isImpersonating 
                    ? (profile?.displayName || `${profile?.firstName || ''} ${profile?.lastName || ''}`.trim() || impersonatedUser?.displayName || 'User')
                    : (user.displayName || 'User')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isImpersonating ? (profile?.email || impersonatedUser?.email) : user.email}
                </Typography>
              </Box>
              {!isImpersonating && (
                <IconButton
                  onClick={handleProfileEdit}
                  sx={{ ml: 'auto' }}
                  color="primary"
                >
                  <EditIcon />
                </IconButton>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Display Name
              </Typography>
              <Typography variant="body1">
                {profile?.displayName || user.displayName || 'Not set'}
              </Typography>
            </Box>
            {profile?.nickname && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Preferred name
                </Typography>
                <Typography variant="body1">
                  {profile.nickname}
                </Typography>
              </Box>
            )}

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Phone Number
              </Typography>
              <Typography variant="body1">
                {profile?.phone || profile?.phoneNumber || 'Not provided'}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Parent/Guardian Name
              </Typography>
              <Typography variant="body1">
                {profile?.parentName || 'Not provided'}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Account Type
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {/* Show Coach chip if user is a coach (via toggle or role approval) */}
                {(profile?.isCoach || profile?.roles?.includes('coach')) && (
                  <Chip
                    label={profile?.approvalStatus?.coach === 'approved' ? 'Coach' : 'Coach (Pending Approval)'}
                    color={profile?.approvalStatus?.coach === 'approved' ? 'success' : 'warning'}
                    size="small"
                    icon={<PersonIcon />}
                  />
                )}
                {/* Show Parent chip if user is a parent */}
                {(profile?.isParent || profile?.roles?.includes('parent') || profile?.role === 'parent') && (
                  <Chip
                    label="Parent"
                    color="primary"
                    size="small"
                    icon={<PersonIcon />}
                  />
                )}
                {/* Show Adult Skater chip */}
                {(profile?.isSkater || profile?.roles?.includes('skater')) && (
                  <Chip
                    label="Adult Skater"
                    color="info"
                    size="small"
                    icon={<PersonIcon />}
                  />
                )}
              </Box>
              
              {/* Show pending coach approval message */}
              {(profile?.isCoach || profile?.roles?.includes('coach')) && profile?.approvalStatus?.coach !== 'approved' && (
                <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                  Your coach role is pending approval from an interclub administrator.
                </Alert>
              )}
            </Box>
          </WidgetCard>
        </Grid>

        {/* Widget: Your Dashboard */}
        <Grid item xs={12} md={6}>
          <WidgetCard
            title="Your Dashboard"
            icon={<TrophyIcon sx={{ color: 'white', fontSize: 28 }} />}
          >
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Access your personalized dashboard to manage your skaters, view competition schedules, and track eligibility.
            </Typography>
            <Button
              variant="contained"
              fullWidth
              onClick={() => navigate('/dashboard')}
              sx={{
                py: 1.5,
                borderRadius: 3,
                fontWeight: 600,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
                }
              }}
            >
              Go to Dashboard
            </Button>
          </WidgetCard>
        </Grid>

        {/* Widget: USFS Account */}
        <Grid item xs={12}>
          <WidgetCard
            title="USFS Account"
            icon={<LinkIcon sx={{ color: 'white', fontSize: 28 }} />}
          >
            {usfsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {usfsError}
              </Alert>
            )}
            <USFSSyncStatus
              linked={Boolean(usfsStatus?.linked)}
              status={usfsStatus?.status}
              verifiedCount={usfsStatus?.verifiedCount || 0}
              loading={usfsLoading}
              onLink={handleOpenUSFSLink}
              onSync={handleManualSync}
              onUnlink={handleUnlink}
              syncing={syncing}
              unlinking={unlinking}
              readOnly={isImpersonating}
              credentialsStale={usfsStatus?.credentialsStale || false}
            />
          </WidgetCard>
        </Grid>

      </Grid>

      {/* Edit Profile Dialog */}
      <Dialog open={editDialogOpen} onClose={handleProfileCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Profile</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Display Name"
              value={profileForm.displayName || ''}
              onChange={(e) => setProfileForm(prev => ({ ...prev, displayName: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Preferred name (optional)"
              value={profileForm.nickname || ''}
              onChange={(e) => setProfileForm(prev => ({ ...prev, nickname: e.target.value }))}
              sx={{ mb: 2 }}
              helperText={profileForm.isCoach ? 'Shown when you\'re listed as a coach (e.g. "Lexi" for Alexis)' : 'How you\'d like to be shown in coach and other listings'}
            />
            <TextField
              fullWidth
              label="Phone Number"
              value={profileForm.phone || ''}
              onChange={(e) => setProfileForm(prev => ({ ...prev, phone: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Parent/Guardian Name"
              value={profileForm.parentName || ''}
              onChange={(e) => setProfileForm(prev => ({ ...prev, parentName: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(profileForm.isSkater)}
                  onChange={(e) => setProfileForm(prev => ({ ...prev, isSkater: e.target.checked }))}
                />
              }
              label="I am also a skater"
              sx={{ mb: 1 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, ml: 4 }}>
              Enable this if you compete as an adult skater. This affects how the navigation labels appear.
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(profileForm.isCoach)}
                  onChange={(e) => setProfileForm(prev => ({ ...prev, isCoach: e.target.checked }))}
                />
              }
              label="I am a coach"
              sx={{ mb: 1 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, ml: 4 }}>
              Enable this if you manage skaters as a coach. This affects how the navigation labels appear.
            </Typography>
            {profileForm.isCoach && (
              <>
                <TextField
                  fullWidth
                  required
                  label="USFS Membership ID (required for coaches)"
                  value={profileForm.coachUsfsMemberId ?? profileForm.membershipNumber ?? ''}
                  onChange={(e) => setProfileForm(prev => ({ ...prev, coachUsfsMemberId: e.target.value, membershipNumber: e.target.value }))}
                  placeholder="Your USFS member number"
                  helperText="Required to link your coach record in the system."
                  sx={{ mb: 2 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(profileForm.isParent)}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, isParent: e.target.checked }))}
                    />
                  }
                  label="I am also a parent/guardian of skaters"
                  sx={{ mb: 1, ml: 2 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, ml: 6 }}>
                  Enable this if you also have children who skate and need to register them.
                  If unchecked, you'll only be able to link to existing skaters by their USFS ID.
                </Typography>
              </>
            )}
            {profileForm.isSkater && (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  These fields are for your personal skating information
                </Alert>
                <TextField
                  fullWidth
                  label="US Figure Skating Membership Number"
                  value={profileForm.membershipNumber || ''}
                  onChange={(e) => setProfileForm(prev => ({ ...prev, membershipNumber: e.target.value }))}
                  helperText={
                    <>
                      Your personal USFS number.{" "}
                      <Link
                        href="https://www.usfsaonline.org/Account/FindMembershipNumber"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Look it up here
                      </Link>
                    </>
                  }
                  sx={{ mb: 2 }}
                  required
                />
                <TextField
                  fullWidth
                  label="Club"
                  value={profileForm.school || ''}
                  onChange={(e) => setProfileForm(prev => ({ ...prev, school: e.target.value }))}
                  helperText="Your skating club"
                  sx={{ mb: 2 }}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleProfileCancel} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleProfileSave} variant="contained" startIcon={<SaveIcon />}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <USFSLinkDialog
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        onSubmit={handleLinkUSFS}
        loading={linking}
        error={linkError}
        initialUsername={usfsStatus?.status?.usfsUsername || ''}
        defaultAutoSync={Boolean(usfsStatus?.status?.autoSync ?? true)}
      />

      {/* Club Selection Dialog for Coach Requests */}
      <Dialog 
        open={clubSelectDialogOpen} 
        onClose={() => setClubSelectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ClubIcon color="primary" />
            Select Your Club
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3, mt: 1 }}>
            To request coach status, please select your club. This ensures your request goes to the right interclub for approval.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Tip: Link your USFS account to automatically fill in your club information.
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Club</InputLabel>
            <Select
              value={selectedClubId}
              onChange={(e) => setSelectedClubId(e.target.value)}
              label="Club"
              disabled={loadingClubs}
            >
              {loadingClubs ? (
                <MenuItem disabled>Loading clubs...</MenuItem>
              ) : (
                clubs.map((club) => (
                  <MenuItem key={club.id} value={club.id}>
                    {club.name || club.shortName}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          {!usfsStatus?.status?.linked && (
            <Button
              variant="outlined"
              onClick={() => {
                setClubSelectDialogOpen(false);
                handleOpenUSFSLink();
              }}
              fullWidth
              sx={{ mt: 1 }}
            >
              Or Link Your USFS Account
            </Button>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClubSelectDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleClubSelect} 
            variant="contained" 
            disabled={!selectedClubId || loadingClubs}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Onboarding Dialog for New Users */}
      <Dialog open={showOnboarding} maxWidth="sm" fullWidth>
        <DialogTitle>
          {onboardingStep === 0 ? '👋 Welcome! Let\'s Set Up Your Account' : '🏆 Add Your Skaters'}
        </DialogTitle>
        <DialogContent>
          {onboardingStep === 0 && (
            <Box sx={{ pt: 2 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                Tell us a bit about yourself so we can personalize your experience.
              </Alert>
              <TextField
                fullWidth
                label="Your Name"
                value={profileForm.displayName || ''}
                onChange={(e) => setProfileForm((prev) => ({ ...prev, displayName: e.target.value }))}
                sx={{ mb: 2 }}
                required
                helperText="This will be used as the parent/guardian name for any skaters you register"
              />
              <TextField
                fullWidth
                label="Phone Number"
                value={profileForm.phone || ''}
                onChange={(e) => setProfileForm((prev) => ({ ...prev, phone: e.target.value }))}
                sx={{ mb: 2 }}
                helperText="Optional - for competition notifications"
              />
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Are you registering yourself or your children?
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(profileForm.isSkater)}
                    onChange={(e) => setProfileForm((prev) => ({ ...prev, isSkater: e.target.checked }))}
                  />
                }
                label="I compete as a skater myself (adult skater)"
                sx={{ mb: 1 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6 }}>
                Enable this if you're an adult who competes. Leave off if you're only registering children.
              </Typography>
            </Box>
          )}
          {onboardingStep === 1 && (
            <Box sx={{ pt: 2 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                Would you like to add your skaters now? You can always add or edit them later from the Skaters page.
              </Alert>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Click "Add Skaters" to go to the Skaters page and register your skaters for the current season.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Or click "Skip for Now" to explore the app first.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {onboardingStep === 0 && (
            <>
              <Button
                onClick={() => {
                  setShowOnboarding(false);
                }}
                color="inherit"
              >
                Skip for Now
              </Button>
              <Button
                variant="contained"
                onClick={async () => {
                  if (!profileForm.displayName) {
                    setError('Please enter a display name');
                    return;
                  }
                  try {
                    const token = await getIdToken();
                    // Auto-set parentName from displayName for parents
                    const profileToSave = {
                      ...profileForm,
                      parentName: profileForm.isSkater ? '' : profileForm.displayName,
                    };
                    await registrationService.updateProfile(profileToSave, token);
                    if (user && profileForm.displayName) {
                      const auth = getAuth(app);
                      if (auth.currentUser) {
                        await updateFirebaseProfile(auth.currentUser, { displayName: profileForm.displayName });
                      }
                    }
                    setProfile(profileToSave);
                    await refreshUserData();
                    setOnboardingStep(1);
                  } catch (err) {
                    setError('Failed to save profile');
                    console.error('Error saving profile:', err);
                  }
                }}
                disabled={!profileForm.displayName}
              >
                Continue
              </Button>
            </>
          )}
          {onboardingStep === 1 && (
            <>
              <Button
                onClick={() => {
                  setShowOnboarding(false);
                }}
                color="inherit"
              >
                Skip for Now
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setShowOnboarding(false);
                  navigate('/skaters');
                }}
              >
                Add Skaters
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
